import { useEffect, useState } from "react";
import Spinner from "../components/Spinner";
import RegenerateBar from "../components/RegenerateBar";
import VendorBrowser from "../marketplace/VendorBrowser";
import { callAI, track, BETA_SECRET } from "../lib/api";
import { renderMD } from "../lib/markdown";
import { getStageState } from "../lib/journey";

// Stage 10 — Offers & negotiation.
//
// Two paths:
//   (A) DIY     — AI scores incoming offers and drafts counter strategy.
//                  Seller decides.
//   (B) Assist  — Pick a marketplace `listing_assistant` to handle negotiation
//                  end-to-end. Seller approves big decisions; pro does the rest.

const MOCK_OFFERS = [
  { id: "A82", price: 1395000, close_days: 30, financing: "30% down conventional, 21d financing contingency", emd: 40000, inspection_contingency: "17 days", appraisal_contingency: "yes" },
  { id: "B11", price: 1420000, close_days: 21, financing: "All cash", emd: 50000, inspection_contingency: "10 days", appraisal_contingency: "waived" },
];

// Loose address match — buyer-submitted offers store whatever address string
// the search result had (often street-only), seller's journey.address is the
// full autocompleted string. Compare on the street-number+name fragment only.
const streetFragment = (address) => (address || "").split(",")[0].trim();

// Real incoming offers for this listing, fetched from the same `offers`
// table the buyer flow writes to (api/offers.js submitOffer) — this is the
// actual buyer-seller connection point, not a display-only mock.
const useRealOffers = (address) => {
  const [offers, setOffers] = useState(null); // null = loading/unknown, [] = none found
  useEffect(() => {
    let cancelled = false;
    const street = streetFragment(address);
    if (!street) { setOffers([]); return; }
    fetch(`/api/offers?address=${encodeURIComponent(street)}`, {
      headers: { "x-beta-token": BETA_SECRET },
    })
      .then((r) => (r.ok ? r.json() : { offers: [] }))
      .then((d) => {
        if (cancelled) return;
        const live = (d.offers || []).filter((o) => o.status === "submitted" || o.status === "countered");
        setOffers(live.map((o) => ({
          id: o.id.slice(0, 8),
          price: (o.offer_price_cents || 0) / 100,
          close_days: o.closing_date
            ? Math.max(0, Math.round((new Date(o.closing_date) - new Date()) / 86400000))
            : null,
          financing: `${o.financing_type || "conventional"}${o.prequal_verified ? " · pre-approval verified" : ""}`,
          emd: (o.earnest_money_cents || 0) / 100,
          inspection_contingency: o.contingency_inspection ? "included" : "waived",
          appraisal_contingency: o.contingency_appraisal ? "included" : "waived",
          buyer_name: o.buyer_name,
          buyer_note: o.buyer_note,
        })));
      })
      .catch(() => { if (!cancelled) setOffers([]); });
    return () => { cancelled = true; };
  }, [address]);
  return offers;
};

const SYSTEM = "You are a real estate negotiation expert. Score and compare offers on net proceeds, risk-weighted close probability, and contingency danger. Recommend a strategy. Be direct. Numbers over words.";

const PROMPT = (address, listPrice, offers, note) => `Property: ${address}
Target list price: $${listPrice ? listPrice.toLocaleString() : "?"}

Incoming offers:
${offers.map((o, i) => `
Offer ${i + 1} (${o.id})
- Price: $${o.price.toLocaleString()}
- Close: ${o.close_days} days
- Financing: ${o.financing}
- EMD: $${o.emd.toLocaleString()}
- Inspection contingency: ${o.inspection_contingency}
- Appraisal contingency: ${o.appraisal_contingency}`).join("")}
${note ? `\nSeller's guidance for this regeneration — factor this in: ${note}\n` : ""}
Output in markdown:

**Side-by-side**
- (one bullet per offer with close-probability score 0-100 and risk-weighted net proceeds)

**Recommended strategy**
(2-3 sentences — which to accept or counter, and how)

**Counter terms for the leading offer**
- (3-4 bullets, specific numbers)`;

export default function OffersStage({ state, journey, update }) {
  const mode = state.data?.mode || null;

  const pickMode = (m) => {
    track("seller_offers_mode_picked", { mode: m });
    update({ data: { ...(state.data || {}), mode: m } });
  };

  if (!mode) {
    return (
      <>
        <p style={{ color: "#5b7365", fontSize: 14, lineHeight: 1.7, margin: "0 0 18px" }}>
          How would you like to handle incoming offers?
        </p>
        <div className="s-confirm-actions">
          <button type="button" className="s-confirm-btn primary" onClick={() => pickMode("diy")}>
            <span>I'll review them · AI scores + drafts counters</span>
            <span className="s-confirm-btn-arrow">→</span>
          </button>
          <button type="button" className="s-confirm-btn ghost" onClick={() => pickMode("assisted")}>
            <span>Have a marketplace pro negotiate for me</span>
            <span className="s-confirm-btn-arrow">→</span>
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div className="s-ai-pill" style={{ margin: 0 }}>
          {mode === "diy" ? "★ AI · Offer scoring + counter strategy" : "★ Marketplace · Pro handles negotiation"}
        </div>
        <button type="button" className="s-link-btn" onClick={() => pickMode(null)}>↻ switch mode</button>
      </div>
      {mode === "diy" ? <DIYView state={state} journey={journey} update={update} /> : <AssistedView state={state} journey={journey} update={update} />}
    </>
  );
}

// ─── DIY ────────────────────────────────────────────────────────────────────
function DIYView({ state, journey, update }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(state.data?.analysis || "");
  const [err, setErr] = useState(null);
  const realOffers = useRealOffers(journey.address);
  const hasReal = realOffers && realOffers.length > 0;
  const offers = hasReal ? realOffers : MOCK_OFFERS;

  const run = async (note = "") => {
    setLoading(true);
    setErr(null);
    track("seller_offers_analyze", { has_note: Boolean(note), real: hasReal });
    try {
      const valData = getStageState(journey, "valuation").data;
      const listPrice = valData?.range?.midpoint;
      const { text } = await callAI({
        system: SYSTEM,
        prompt: PROMPT(journey.address, listPrice, offers, note),
        maxTokens: 1200,
      });
      setAnalysis(text);
      update({
        status: "done",
        data: { ...(state.data || {}), mode: "diy", analysis: text, offers, generated_at: new Date().toISOString() },
      });
      track("seller_offers_done");
    } catch {
      setErr("Analysis failed. Try again.");
    }
    setLoading(false);
  };

  return (
    <>
      <p style={{ color: "#5b7365", fontSize: 14, lineHeight: 1.7, margin: "0 0 14px" }}>
        {hasReal
          ? `${offers.length} real offer${offers.length > 1 ? "s" : ""} on the table. Run AI side-by-side scoring to see net proceeds, close probability, and contingency risk — then get a counter strategy.`
          : "No real offers yet — showing sample offers. Run AI side-by-side scoring to see net proceeds, close probability, and contingency risk — then get a counter strategy."}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10, marginBottom: 18 }}>
        {offers.map((o) => (
          <div key={o.id} className="s-vendor passive">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <div className="s-vendor-name">{o.buyer_name || `Buyer #${o.id}`}</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, color: "#1c5448" }}>
                ${o.price.toLocaleString()}
              </div>
            </div>
            <div className="s-vendor-meta">{o.close_days != null ? `${o.close_days}d close · ` : ""}EMD ${o.emd.toLocaleString()}</div>
            <div className="s-vendor-meta" style={{ marginTop: 4, fontFamily: "'Outfit',sans-serif", letterSpacing: 0, color: "#5b7365", lineHeight: 1.5 }}>
              {o.financing}
            </div>
            <div className="s-vendor-meta" style={{ marginTop: 2, fontFamily: "'Outfit',sans-serif", letterSpacing: 0, color: "#5b7365" }}>
              Inspection {o.inspection_contingency} · Appraisal {o.appraisal_contingency}
            </div>
            {o.buyer_note && (
              <div className="s-vendor-meta" style={{ marginTop: 6, fontFamily: "'Outfit',sans-serif", letterSpacing: 0, color: "#5b7365", fontStyle: "italic", lineHeight: 1.5 }}>
                "{o.buyer_note}"
              </div>
            )}
          </div>
        ))}
      </div>

      {!analysis && (
        <button type="button" className="s-btn amber" onClick={() => run()} disabled={loading}>
          {loading ? "Analyzing…" : "Run AI analysis →"}
        </button>
      )}
      {analysis && (
        <RegenerateBar
          onRegenerate={(note) => run(note)}
          loading={loading}
          placeholder="e.g. weigh close speed more than price…"
        />
      )}

      {loading && <div style={{ marginTop: 16 }}><Spinner label="Scoring offers and drafting counter strategy…" /></div>}

      {err && !loading && <div className="s-intake-err" style={{ marginTop: 12 }}>{err}</div>}

      {analysis && !loading && (
        <div className="s-prose" style={{ marginTop: 18 }} dangerouslySetInnerHTML={{ __html: renderMD(analysis) }} />
      )}

    </>
  );
}

// ─── Assisted ───────────────────────────────────────────────────────────────
function AssistedView({ state, journey, update }) {
  const isAccepted = Boolean(state.data?.accepted_at);
  return (
    <>
      <p style={{ color: "#5b7365", fontSize: 14, lineHeight: 1.7, margin: "0 0 14px" }}>
        Pick a marketplace negotiator. They review every incoming offer, draft counters, and only
        come to you for the big "accept this final terms?" decision.
      </p>

      <VendorBrowser
        type="listing_assistant"
        journey={journey}
        state={state}
        update={update}
        stageId="offers"
      />

      {isAccepted && (
        <div className="s-soon" style={{ marginTop: 18 }}>
          <div className="s-soon-row">
            <div className="s-soon-bullet">✓</div>
            <div className="s-soon-text">
              <strong>{state.data.picked_vendor_name}</strong> is now handling offer negotiation.
              You'll get a notification with their recommendation for each incoming offer; we won't
              accept anything without your one-tap approval.
            </div>
          </div>
        </div>
      )}

      {/* Same mock offers, but framed as "what your pro is reviewing". */}
      {isAccepted && (
        <>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#9aa9a0", letterSpacing: "0.08em", margin: "22px 0 8px", textTransform: "uppercase" }}>
            Offers under review by your pro
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10 }}>
            {MOCK_OFFERS.map((o) => (
              <div key={o.id} className="s-vendor passive">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                  <div className="s-vendor-name">Buyer #{o.id}</div>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, color: "#1c5448" }}>
                    ${o.price.toLocaleString()}
                  </div>
                </div>
                <div className="s-vendor-meta">{o.close_days}d close · EMD ${o.emd.toLocaleString()}</div>
                <div className="s-vendor-meta green" style={{ marginTop: 6 }}>
                  Status: pro is drafting counter
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
